// Holiday & Expiry Calendar feed (God 2026-10-20, Dussehra: the header showed a normal
// timer on an exchange holiday because every widget kept its own clock).
// One shared poll of /expiries/calendar (FastAPI :5001, market_session.calendar_view +
// Dhan scrip master); every subscriber reads the same snapshot.
import { useEffect, useState } from 'react';

export type VenueState = {
  state: string;            // HOLIDAY | WEEKEND | CLOSED | PRE_OPEN | ... | OPEN | POST_CLOSE
  entries_allowed: boolean;
  reason: string;
  message: string;
  open_at: string;
  close_at: string;
  seconds_to_open: number | null;
  holiday?: string;
  holiday_partial?: { morning: string; evening: string; split: string };
};
export type HolidayRow = {
  date: string; exchange: string; name: string; type: 'FULL' | 'PARTIAL';
  morning: string; evening: string; evening_open?: string;
  weekday: string; weekend: boolean; days_away: number;
};
export type ExpiryRow = { date: string; weekday: string; days_away: number; kind: 'WEEKLY' | 'MONTHLY'; today: boolean };
export type SymbolExpiries = { symbol: string; exchange: string; group: 'INDEX' | 'MCX'; options: ExpiryRow[]; futures: ExpiryRow[] };
export type MarketCalendar = {
  status: string; today: string; ist: string;
  venues: Record<'NSE' | 'BSE' | 'MCX', VenueState>;
  holidays: HolidayRow[];
  expiries: SymbolExpiries[];
  next_expiries: (ExpiryRow & { symbol: string; exchange: string; segment: 'OPT' | 'FUT' })[];
  calendar_version?: string; calendar_updated?: string; expiry_source?: string; error?: string | null;
  timings?: {
    NSE?: { open_bell: string; cash_close: string; fno_close: string; closing_auction?: string; system_no_new_entry: string };
    BSE?: { open_bell: string; cash_close: string; fno_close: string };
    MCX?: { open_bell: string; close: string; us_dst: boolean; system_no_new_entry: string };
    opening_bell_guard_min?: number;
  };
  fetchedAt: number;
};

const CAL_URL = 'https://api.jaiswalcapital.com/expiries/calendar?days=120&per_symbol=4';
let snapshot: MarketCalendar | null = null;
const subs = new Set<(c: MarketCalendar | null) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let lastDay = '';

async function poll() {
  try {
    const r = await fetch(CAL_URL, { signal: AbortSignal.timeout(10000) });
    if (!r.ok) return;
    const d = await r.json();
    if (d?.status !== 'success') return;
    snapshot = { ...d, fetchedAt: Date.now() };
    subs.forEach(fn => fn(snapshot));
  } catch { /* keep the last snapshot; callers fall back to their local clock */ }
}

function istDay() { return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10); }

export function useMarketCalendar(): MarketCalendar | null {
  const [cal, setCal] = useState<MarketCalendar | null>(snapshot);
  useEffect(() => {
    subs.add(setCal);
    if (!timer) {
      poll();
      // 60 s is enough: the server verdict only changes at session boundaries; a day roll
      // forces an immediate refresh so yesterday's holiday never leaks into today.
      timer = setInterval(() => {
        if (document.hidden) return;
        const d = istDay();
        if (d !== lastDay) { lastDay = d; poll(); return; }
        poll();
      }, 60_000);
    }
    return () => {
      subs.delete(setCal);
      if (!subs.size && timer) { clearInterval(timer); timer = null; }
    };
  }, []);
  // A snapshot from another IST day is stale for the session verdict.
  return cal && cal.today === istDay() ? cal : null;
}

/** Server verdict for a venue, or null when the calendar is unavailable/stale. */
export function venueHoliday(cal: MarketCalendar | null, venue: 'NSE' | 'BSE' | 'MCX'):
  { full: boolean; partialMorning: boolean; partialEvening: boolean; name: string; v: VenueState } | null {
  const v = cal?.venues?.[venue];
  if (!v) return null;
  const full = v.state === 'HOLIDAY';
  const p = v.holiday_partial;
  const partialMorning = !!p && p.morning === 'closed';
  const partialEvening = !!p && p.evening === 'closed';
  if (!full && !partialMorning && !partialEvening) return null;
  return { full, partialMorning, partialEvening, name: v.holiday || '', v };
}
